import { useEffect, useState } from "react";
import logoUrl from "./assets/logo1.png";
import { applyTheme } from "./themeUtils";

const pricingPlans = [
  {
    name: "Standard",
    subtitle: "For smaller events",
    price: "-",
    limit: "10 files / person",
    validity: "30 days",
    ideal: "Baptisms, birthdays, intimate gatherings",
    featured: false,
  },
  {
    name: "Plus",
    subtitle: "Most popular choice",
    price: "-",
    limit: "20 files / person",
    validity: "60 days",
    ideal: "Weddings and parties with 100–350 guests",
    featured: true,
  },
  {
    name: "Pro / Unlimited",
    subtitle: "For larger events",
    price: "-",
    limit: "Unlimited or 40 files / person",
    validity: "180 days",
    ideal: "Large weddings, events with 400+ guests",
    featured: false,
  },
];

const addOnOptions = [
  { id: "personalization", label: "Visual page personalization", price: "-RON" },
  { id: "qr-kit", label: "Printable QR table kit", price: "-RON" },
  { id: "storage", label: "Extended storage validity (3 months)", price: "-RON" },
  { id: "vip", label: "VIP package bundle – includes full visual personalization, QR table kit, and 90 extra days of storage", price: "-RON" },
];

const steps = [
  {
    number: "01",
    title: "We handle the design for you",
    text: "You send us the event details, and we create a fully tailored page with the right colors, background, and overall style for your occasion.",
  },
  {
    number: "02",
    title: "You receive a QR code for the tables",
    text: "We prepare a QR code ready to print and display at the event, so guests can open the upload page in seconds with no app needed.",
  },
  {
    number: "03",
    title: "Guests upload their content",
    text: "At the event, guests scan the code and upload their photos and videos directly from their phones, keeping everything simple and seamless.",
  },
];

const features = [
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.8l1.8 4.7 4.7 1.8-4.7 1.8-1.8 4.7-1.8-4.7-4.7-1.8 4.7-1.8L12 2.8z" />
      </svg>
    ),
    title: "Done-for-you design",
    text: "We personalize the event page for you with the right look, colors, and background to match your celebration perfectly.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
        <circle cx="12" cy="16.5" r="1.2" />
      </svg>
    ),
    title: "Optimized for mobile",
    text: "Guests upload directly from their phones in a smooth, simple browser experience without needing any app or setup.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="5" y="10" width="14" height="11" rx="2.5" />
        <path d="M8.5 10V7.8A3.5 3.5 0 0 1 12 4.5a3.5 3.5 0 0 1 3.5 3.3V10" />
      </svg>
    ),
    title: "100% private gallery",
    text: "Only the event host can access the full collection. Guests can upload content, while the gallery remains private and protected.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 15.5c2.1-3.4 4.4-5.1 7.5-5.1s5.4 1.7 7.5 5.1" />
        <path d="M8 18.5c1-1.1 2.1-1.7 4-1.7s3 .6 4 1.7" />
        <path d="M12 9.5V4.8M9.6 6.7l2.4-2.2 2.4 2.2" />
      </svg>
    ),
    title: "Live uploads",
    text: "New photos and videos appear in the gallery in real time, making it easy to follow the event as it unfolds.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="11" rx="2" />
        <path d="M8 19h8M10 16v3M14 16v3" />
        <path d="M8 9.2l2.2 2.2 4.2-4.4 2.1 2.2" />
      </svg>
    ),
    title: "Photos & videos",
    text: "Capture every moment in one place with both still images and video clips collected from the event itself.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 8.5V7.5A4 4 0 0 1 12 3.5a4 4 0 0 1 4 4v1" />
        <rect x="5" y="8.5" width="14" height="12" rx="2.5" />
        <path d="M8.5 13.8h7M8.5 17h5" />
      </svg>
    ),
    title: "Full ZIP download",
    text: "Download all files at original quality in a single click as a complete ZIP archive, with quick options to remove any unwanted frames afterward.",
  },
];

export default function EventIndex() {
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  useEffect(() => {
    applyTheme("ivoryGold");
  }, []);

  const toggleAddOn = (id) => {
    setSelectedAddOns((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <div className="event-index-page">
      <div className="event-header-shell">
        <div className="container">
          <nav className="event-top-nav" aria-label="Main navigation">
            <div className="event-brand-row">
              <img src={logoUrl} alt="EventSnap" className="event-brand-logo" />
              <span className="event-brand-name">Event<span>Snap</span></span>
            </div>

            <div className="event-nav-links">
              <a href="#how-it-works">How it works</a>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
            </div>

            <a href="#pricing" className="event-nav-cta">Get started</a>
          </nav>
        </div>
      </div>

      <section className="event-hero">
        <div className="event-hero-glow" />
        <div className="container event-hero-inner">
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <div className="event-badge">Event photo collection</div>

              <h1 className="event-hero-title">
                Collect every <span>memory</span>
              </h1>

              <p className="event-hero-copy">
                We create a dedicated event page for you, fully tailored to your occasion, with the design, colors, and background customized to fit the atmosphere and the guest experience.
              </p>

              <div className="event-hero-actions">
                <a href="#how-it-works" className="event-primary-btn">
                  Learn more
                </a>
                <a href="#features" className="event-secondary-btn">
                  Explore features
                </a>
              </div>

              <div className="event-hero-stats">
                <div>
                  <strong>Instant</strong>
                  <span>uploads</span>
                </div>
                <div>
                  <strong>Private</strong>
                  <span>gallery</span>
                </div>
                <div>
                  <strong>QR</strong>
                  <span>table code</span>
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="event-preview-card">
                <div className="event-preview-header">
                  <div className="event-preview-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="event-preview-label">Guest upload</span>
                </div>

                <div className="event-preview-body">
                  <h3>Sarah & Daniel</h3>
                  <p>Wedding — 14 September 2026</p>

                  <div className="event-preview-panel">
                    <div className="event-preview-panel-title">Upload Your Photos/Videos</div>
                    <div className="event-preview-panel-sub">Browse to upload (max 10)</div>
                    <div className="event-preview-droparea">
                      <span>Drop your content here</span>
                    </div>
                    <button type="button" className="event-preview-button">Browse Files</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="event-steps-section">
        <div className="container">
          <div className="event-section-heading">
            <span className="event-kicker">How it works</span>
            <h2>Simple process for your event</h2>
          </div>

          <div className="row g-4">
            {steps.map((step) => (
              <div key={step.number} className="col-lg-4">
                <div className="event-step-card">
                  <div className="event-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="event-features-section">
        <div className="container">
          <div className="event-section-heading center">
            <span className="event-kicker">Why EventSnap</span>
            <h2>Built for real events and real guests.</h2>
          </div>

          <div className="row g-4">
            {features.map((feature) => (
              <div key={feature.title} className="col-md-6 col-lg-4">
                <div className="event-feature-card">
                  <div className="event-feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="event-pricing-preview-section">
        <div className="container">
          <div className="event-section-heading center">
            <span className="event-kicker">Pricing</span>
            <h2>Choose the package that fits your event.</h2>
          </div>

          <div className="event-pricing-grid landing">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`event-plan-card ${plan.featured ? "featured" : ""}`}>
                {plan.featured && <div className="event-plan-badge">Recommended</div>}
                <div className="event-plan-header">
                  <span className="event-plan-name">{plan.name}</span>
                  <span className="event-plan-subtitle">{plan.subtitle}</span>
                </div>

                <div className="event-plan-price-row">
                  <span className="event-plan-price">{plan.price}</span>
                  <span className="event-plan-currency">RON</span>
                </div>

                <ul className="event-plan-list">
                  <li>
                    <strong>Upload limit:</strong> {plan.limit}
                  </li>
                  <li>
                    <strong>Archive validity:</strong> {plan.validity}
                  </li>
                  <li>
                    <strong>Includes:</strong> QR code, private gallery, photo &amp; video upload, full ZIP download
                  </li>
                  <li>
                    <strong>Ideal for:</strong> {plan.ideal}
                  </li>
                </ul>

                <button type="button" className="event-plan-button">
                  Select plan
                </button>
              </div>
            ))}
          </div>

          <div className="event-upsell-panel event-home-upsells">
            <div className="event-upsell-header">
              <span>Extra add-ons</span>
            </div>
            <div className="event-upsell-list">
              {addOnOptions.map((option) => {
                const checked = selectedAddOns.includes(option.id);
                const isWide = option.id === "storage" || option.id === "vip";

                return (
                  <label
                    key={option.id}
                    className={`event-upsell-item ${isWide ? "event-upsell-item--wide" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAddOn(option.id)}
                    />
                    <span>
                      {option.label}
                      <small>{option.price}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="event-cta-section">
        <div className="container event-cta-box">
          <div>
            <span className="event-kicker light">Ready to begin</span>
            <h2>Create a more memorable event experience.</h2>
          </div>
          <a href="" className="event-primary-btn dark">
            Get started
          </a>
        </div>
      </section>

      <footer className="event-footer">
        <div className="container">
          <span>Powered by EventSnap</span>
        </div>
      </footer>
    </div>
  );
}